<template>
  <div :class="canDownload() ? 'viewer-download' : 'viewer'">
    <Component
      v-if="file"
      :is="viewer"
      :file="file"
      @fallback-viewer="loadDICOMFallback"
      :account-id="accountID"
      :token="token"
      :root-nav-path="rootNavPath"
      :root-nav-current="rootNavCurrent"
      @navPath="onNavPath"
      @navCurrent="onNavCurrent"
      @back="goBack"
    />
    <footer class="footer" v-if="canDownload()">
      <div
        class="pa-0 ma-0">
        <p v-if="downloading" class="secondary text-center download-info">
          <v-progress-linear
            indeterminate
            color="primary"
          ></v-progress-linear>
          {{$t('Downloading').toUpperCase()}}</p>
        <p v-else-if="downloaded" class="success text-center download-info">
          {{$t('Downloaded').toUpperCase()}}
        </p>
        <v-btn v-else class="download-btn" block color="primary" @click="downloadHandler">
          <v-icon>mdi-arrow-down</v-icon>
          {{$t('Download')}}
        </v-btn>
        <div @click="disableDownload" class="text-center close-download">
            <v-icon color="white">mdi-close</v-icon>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
  import jwtDecode from 'jwt-decode'
  import { getParameterByName } from '@/utils'

  export default {
    name: 'viewer',
    data () {
      return {
        viewer: null,
        file: null,
        rootNavPath: [],
        rootNavCurrent: {},
        origin: null,
        downloader:null,
        downloading: false,
        downloaded: false,
        retries: 0,
      }
    },
    created () {
      if (this.inIframe) {
        window.parent.postMessage({ type: 'idonia-viewer-ready' }, '*')
      }

      window.addEventListener('message', event => {
        const data = event.data

        if (event.origin === data.origin) {
          switch (data.type) {
            case 'idonia-setup-nav':
              this.origin = data.origin
              this.rootNavCurrent = data.data.rootNavCurrent
              this.rootNavPath = data.data.rootNavPath
              break
            case 'idonia-change-route':
              this.$router.push(data.data.path)
              break
          }
        }
      })
    },
    watch: {
      '$route.params.file_id': {
        immediate: true,
        async handler (fileID) {
          if (!this.isVaultIdToken && fileID && fileID !== 'undefined') {
            const { data } = await this.axios.get(`/file/${fileID}`).then(response=>response)
            this.file = data
            this.setDownloader()
            await this.loadViewer()
          }
        }
      },
      idToken: {
        immediate: true,
        async handler (token) {
          if (this.isVaultIdToken) {
            let type = 'idonia'
            if (Object.prototype.hasOwnProperty.call(this.decodedJwt, 'idonia.vault.type')) {
              type = Object.prototype.hasOwnProperty.call(this.decodedJwt, 'idonia.vault.type')
            }

            const studyInstanceUID = this.decodedJwt['idonia.vault.object_uid'];
            const studyInstanceUIDArray = this.decodedJwt['idonia.vault.objects'];

            let StudyInstanceUIDs = [];
            if (studyInstanceUIDArray && studyInstanceUIDArray.length) {
              StudyInstanceUIDs = studyInstanceUIDArray.map(
                item => item['idonia.vault.objects.object_uid']
              );
            }

            if (studyInstanceUID && studyInstanceUID.length) {
              StudyInstanceUIDs = studyInstanceUID.split(',');
            }

            let fileType = 'document_vault'
            try {
              await this.axios.get(this.decodedJwt['idonia.vault.url'] + '/document/' + this.decodedJwt['idonia.vault.object_uid'] + '/info', {
                headers: {
                  Authorization: 'Bearer ' + token
                }
              })
            } catch (e) {
              fileType = 'dicom_vault'
            }

            this.file = {
              type: fileType,
              [fileType]: {
                type: type,
                url: this.decodedJwt['idonia.vault.url'],
                auth_token: token,
                [fileType === 'dicom_vault' ? 'study_uid' : 'uuid']: StudyInstanceUIDs[0]
              }
            }

            await this.loadViewer()
          }
        }
      }
    },
    computed: {
      idToken () {
        return getParameterByName('id_token')
      },
      decodedJwt () {
        if (this.idToken) {
          return jwtDecode(this.idToken)
        }

        return ''
      },
      isVaultIdToken () {
        return (
          Object.prototype.hasOwnProperty.call(this.decodedJwt, 'idonia.vault.url') &&
          (
            Object.prototype.hasOwnProperty.call(this.decodedJwt, 'idonia.vault.object_uid') ||
            Object.prototype.hasOwnProperty.call(this.decodedJwt, 'idonia.vault.objects')
          )
        )
      },
      token () {
        return this.$route.query.token
      },
      accountID () {
        return parseInt(this.$route.query.account_id)
      },
      inIframe () {
        return window.location !== window.parent.location
      },
      idoniaViewerPro () {
        if (typeof this.$route.query.idonia_viewer_pro === 'undefined') {
          return false
        }

        return this.$route.query.idonia_viewer_pro === 'true'
      },
      customViewerUrl () {
        let urlParams = new URLSearchParams(window.location.search)
        return urlParams.get('customViewerUrl') || null
      }
    },
    methods: {
      canDownload () {
        return this.$config.downloadAllowed && !!this.downloader
      },
      onNavPath (path) {
        if (this.inIframe) {
          window.parent.postMessage({
            type: 'idonia-nav-path',
            data: path
          }, this.origin)
        }
      },
      onNavCurrent (path) {
        if (this.inIframe) {
          window.parent.postMessage({
            type: 'idonia-nav-current',
            data: path
          }, this.origin)
        }
      },
      async loadViewer () {
        if (!this.file) {
          this.viewer = () => import('../components/viewers/DummyViewer.vue')
          return
        }
        switch (this.file.type) {
          case 'container_dicom': {
              const { data } = await this.axios.get(`/container_dicom/${this.file.id_file}`)
              this.file = data

              this.viewer = () => import('../components/viewers/Legacy/HTML5DICOMViewer.vue')
            break
          }
          case 'dicom_vault': {
            if (this.customViewerUrl) {
              this.viewer = () => import('../components/viewers/CustomViewer.vue')
            } else if (this.idoniaViewerPro) {
              this.viewer = () => import('../components/viewers/DICOMViewer.vue')
            } else {
              this.viewer = this.file.dicom_vault.type === 'idonia'
                ? () => import('../components/viewers/Legacy/HTML5RemoteDICOMViewer.vue')
                : () => import('../components/viewers/Legacy/HTML5WadoDICOMViewer.vue')
            }
            break
          }
          case 'document_vault': {
            if (this.idoniaViewerPro) {
              this.viewer = () => import('../components/viewers/DICOMViewer.vue')
            } else {
              const { data } = await this.axios.get(this.file.document_vault.url + '/document/' + this.file.document_vault.uuid + '/info', {
                  headers: {
                    Authorization: 'Bearer ' + this.file.document_vault.auth_token
                  }
                })

                if (data && data['mime-type']) {
                  const documentUrl = `${this.file.document_vault.url}/document/${this.file.document_vault.uuid}?token=${this.file.document_vault.auth_token}`

                  this.file = {
                    urls: {
                      HD: documentUrl,
                      orig: documentUrl
                    }
                  }
                  switch (data['mime-type']) {
                    case 'image/jpeg':
                    case 'image/gif':
                    case 'image/bmp':
                    case 'image/png':
                    case 'image/tiff':
                      this.viewer = () => import('../components/viewers/ImageViewer.vue')
                      break
                    case 'video/webm':
                    case 'video/ogg':
                    case 'video/mp4':
                    case 'video/x-msvideo':
                    case 'video/quicktime':
                      this.viewer = () => import('../components/viewers/VideoViewer.vue')
                      break
                    case 'model/stl':
                      this.viewer = () => import('../components/viewers/3DViewer.vue')
                      break
                    case 'application/xml':
                      this.viewer = () => import('../components/viewers/XMLViewer.vue')
                      break
                    case 'application/pdf':
                      this.viewer = () => import('../components/viewers/PDFViewer.vue')
                      break
                    case 'application/vnd.ms-excel':
                    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                    case 'application/msword':
                    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                    case 'application/vnd.ms-powerpoint':
                    case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
                      this.viewer = () => import('../components/viewers/OfficeViewer.vue')
                      break
                    default:
                      this.viewer = () => import('../components/viewers/DummyViewer.vue')
                  }
                }
            }
            break
          }
          case 'document': {
            const { data } = await this.axios.get('document/' + this.file.id_file)
            this.file = data
            switch (data.type) {
              case 'pdf':
                this.viewer = () => import('../components/viewers/PDFViewer.vue')
                break
              case 'xml':
                this.viewer = () => import('../components/viewers/XMLViewer.vue')
                break
              case 'image':
                this.viewer = () => import('../components/viewers/ImageViewer.vue')
                break
              case '3d':
                this.viewer = () => import('../components/viewers/3DViewer.vue')
                break
              case 'video':
                this.viewer = () => import('../components/viewers/VideoViewer.vue')
                break
              default:
                switch (this.file.extension.toLowerCase()) {
                  case 'xls':
                  case 'xlsx':
                  case 'doc':
                  case 'docx':
                  case 'ppt':
                  case 'pptx':
                    this.viewer = () => import('../components/viewers/OfficeViewer.vue')
                    break
                  default:
                    this.viewer = () => import('../components/viewers/DummyViewer.vue')
                }
            }
            break
          }
          default:
            this.viewer = () => import('../components/viewers/DummyViewer.vue')
        }
      },
      disableDownload () {
        this.$config.downloadAllowed = false
        this.$forceUpdate()
      },
      setDownloader(){
        switch (this.file.type) {
          case 'container_dicom': {
            break
          }
          case 'dicom_vault': {
            this.downloader = () => {
              return this.axios.get(`${this.file.dicom_vault.url}/idonia/studies/${this.file.dicom_vault.study_uid}/download`, {
                responseType: 'blob',
                headers: {
                  Authorization: `Bearer ${this.file.dicom_vault.auth_token}`
                }
              }).then(response => ({
                data: response.data,
                type: 'application/zip',
                name: `${this.file.dicom_vault.study_uid}.zip`
              }))
            }
            break
          }
          case 'document_vault': {
            break
          }
          case 'document': {
            this.downloader = () => {
              console.log(this.file.dicom_vault.auth_token)
              return this.axios.get(`${this.$config.apiUrl}document/${this.file.file_uuid}/download`, {
                responseType: 'blob'
              }).then(response => ({
                data: response.data,
                name: this.file.name
              }))
            }
            break
          }
          default:
           this.downloader = null
        }
      },
      downloadHandler() {
        if (!this.downloader){
          return
        }
        this.error = false
        const timer = 2000 * this.retries
        this.downloading = true
        setTimeout(()=>{
          this.downloader().then(({ data, type, name }) => {
              const blob = new Blob([data], type ? { type } : {})
              const url = window.URL.createObjectURL(blob)
              const link = document.createElement('a')
              link.href = url
              link.setAttribute('download', name)
              document.body.appendChild(link)
              link.click()
              document.body.removeChild(link)
            this.downloaded = true
            setTimeout(() => {
              this.downloaded = false
            }, 6000)
            }).catch((e)=>{
            console.error(e)
            this.error = e
            this.retries++
          }).finally(()=>{
            this.downloading = false
            if (this.retries > 6) {
              console.log('Max retries reached') //TODO: show error?
              this.retries=0
            }
          })
        }, timer)
      },
      goBack() {
        let lastElem = this.rootNavPath.slice(-1)[0]
        window.parent.postMessage({
          type: 'idonia-back-element',
          data: lastElem.link
        }, this.origin)
      },
      async loadDICOMFallback () {
        switch (this.file.type) {
          case 'dicom_vault':
            if (this.idoniaViewerPro) {
              this.viewer = () => import('../components/viewers/DICOMViewer.vue')
            } else {
              this.viewer = this.file.dicom_vault.type === 'idonia'
                ? () => import('../components/viewers/Legacy/HTML5RemoteDICOMViewer.vue')
                : () => import('../components/viewers/Legacy/HTML5WadoDICOMViewer.vue')
            }
            break
          default:
            this.loadViewer()
        }
      }
    }
  }
</script>

<style lang="scss">
  .viewer {
    width: 100%;
    height: 100%;
  }
  .viewer-download{
    height: calc(100% - 24px);
    width: 100%;
  }
  .footer{
    width: 100%;
    height: 24px;
    max-height: 24px;
    position: absolute;
    bottom: 0;
  }
  .download-btn .v-btn__content {
    align-self: baseline;
  }
  .download-info{
    color: white;
    font-weight: 500;
    font-family: Roboto, sans-serif;
    height: 24px;
    margin: 0 !important;
    padding: 0;
  }
  .close-download{
    cursor: pointer;
    position: absolute;
    right: 0;
    bottom: 0;
    height: 24px !important;
    padding: 0 15px !important;
  }
</style>
